import {subDays} from "date-fns";
import * as Yup from "yup";

import {
  CustomRepeatType,
  DurationUnit,
  EndType,
  FrequencyType,
  MonthlyRepeatMode,
} from "@/pages/maintenance/maintenance-create/tabs/general/MaintenanceScheduleGeneralTab.types.ts";
import {DEFAULT_NAME_PLACEHOLDER} from "@/pages/maintenance/maintenance-create/tabs/general/subComponents/MaintenanceNameField/MaintenanceNameField.tsx";

export const MaintenanceScheduleGeneralSchema = Yup.object({
  name: Yup.string().required("This field cannot be blank."),
  pause_checks_during_maintenance: Yup.boolean().defined(),
  start_date: Yup.date()
    .required("Start date is required.")
    .test(
      "start-date-not-ended",
      "Maintenance window has already ended. Please choose a start date/time in the future.",
      function (value) {
        const {frequency_type, duration_value, duration_unit} = this.parent;
        if (frequency_type !== FrequencyType.DONT_REPEAT || !value || !duration_value) {
          return true;
        }
        const minutesPerUnit =
          duration_unit === DurationUnit.DAYS ? 24 * 60 : duration_unit === DurationUnit.HOURS ? 60 : 1;
        const endTime = value.getTime() + duration_value * minutesPerUnit * 60 * 1000;
        return endTime > Date.now();
      }
    ),
  end_date: Yup.date().nullable(),
  duration_value: Yup.number()
    .required("Duration is required.")
    .min(1, "Duration cannot be 0.")
    .typeError("Duration must be a number."),
  duration_unit: Yup.mixed<DurationUnit>().oneOf(Object.values(DurationUnit)).required("Duration unit is required."),
  frequency_type: Yup.mixed<FrequencyType>().oneOf(Object.values(FrequencyType)).required("Frequency is required."),
  rrule_interval: Yup.number()
    .transform((value, original) => (original === "" || original === null || Number.isNaN(value) ? null : value))
    .nullable()
    .when("frequency_type", {
      is: FrequencyType.CUSTOM,
      then: (schema) =>
        schema
          .required("Repeat frequency is required.")
          .min(1, "Repeat frequency must more than 0.")
          .typeError("Repeat frequency must more than 0."),
    }),
  custom_repeat_type: Yup.mixed<CustomRepeatType>().oneOf(Object.values(CustomRepeatType)).nullable(),
  monthly_repeat_mode: Yup.mixed<MonthlyRepeatMode>().oneOf(Object.values(MonthlyRepeatMode)).nullable(),
  end_type: Yup.mixed<EndType>().oneOf(Object.values(EndType)).nullable(),
  rrule_byweekday: Yup.array().of(Yup.string()).nullable(),
  rrule_bymonthday: Yup.array().of(Yup.number()).nullable(),
  rrule_count: Yup.number()
    .transform((value, original) => (original === "" ? null : value))
    .nullable()
    .when("end_type", {
      is: EndType.AFTER,
      then: (schema) =>
        schema.required("Number of occurrences is required.").min(1, "Number of occurrences must be above 0."),
    }),
  rrule_until: Yup.date()
    .nullable()
    .when("end_type", {
      is: EndType.ON,
      then: (schema) =>
        schema.required("End date is required.").min(subDays(new Date(), 1), "End date must be in the future."),
    }),
  tags: Yup.array().of(Yup.number()).defined(),
});

export const MaintenanceScheduleGeneralDefaultValues = {
  name: DEFAULT_NAME_PLACEHOLDER,
  pause_checks_during_maintenance: true,
  start_date: new Date(),
  end_date: undefined,
  duration_value: 60,
  duration_unit: DurationUnit.MINUTES,
  frequency_type: FrequencyType.DONT_REPEAT,
  rrule_interval: 1,
  custom_repeat_type: CustomRepeatType.DAY,
  monthly_repeat_mode: MonthlyRepeatMode.DAY_OF_MONTH,
  end_type: EndType.NEVER,
  rrule_byweekday: [],
  rrule_bymonthday: [],
  rrule_count: null,
  rrule_until: undefined,
  tags: [],
};
